import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, GitBranch, AlertCircle, ChevronDown, ChevronRight, Edit2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recursive BOM Tree Node with inline editing
const BomTreeNode = ({ 
  item, 
  items, 
  boms, 
  level, 
  expandedItems, 
  toggleExpand, 
  onAddComponent,
  onDeleteComponent,
  onUpdateQuantity,
  parentBom = null
}) => {
  const [isEditingQty, setIsEditingQty] = useState(false);
  const [editedQty, setEditedQty] = useState(parentBom?.qty_per || 1);

  const children = boms.filter(b => b.parent_item_id === item.id);
  const isExpanded = expandedItems[`tree-${item.id}`];
  const hasChildren = children.length > 0;
  
  // Only FG and SFG can have components added
  const canAddComponent = item.category === 'FG' || item.category === 'SFG';

  const getCategoryColor = (category) => {
    const colors = {
      'FG': 'bg-green-100 text-green-800',
      'SFG': 'bg-blue-100 text-blue-800',
      'RM': 'bg-orange-100 text-orange-800',
      'BOP': 'bg-purple-100 text-purple-800',
      'RM-Sheet': 'bg-orange-100 text-orange-800',
      'RM-Tube': 'bg-orange-100 text-orange-800',
      'RM-Bar': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const handleSaveQty = async () => {
    if (!parentBom) return;
    
    const newQty = parseFloat(editedQty);
    if (isNaN(newQty) || newQty <= 0) {
      toast.error('Quantity must be a positive number');
      return;
    }

    await onUpdateQuantity(parentBom.id, newQty);
    setIsEditingQty(false);
  };

  const handleCancelEdit = () => {
    setEditedQty(parentBom?.qty_per || 1);
    setIsEditingQty(false);
  };

  return (
    <div className="space-y-2">
      <div className={`flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 border ${level > 0 ? 'ml-6 border-slate-200' : 'border-slate-300 bg-white'}`}>
        {/* Expand/Collapse Icon */}
        <div 
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => hasChildren && toggleExpand(`tree-${item.id}`)}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown className="w-5 h-5 text-slate-600" /> : <ChevronRight className="w-5 h-5 text-slate-600" />
          ) : (
            <div className="w-5 h-5" />
          )}
        </div>
        
        {/* Category Badge */}
        <Badge className={getCategoryColor(item.category)}>
          {item.category}
        </Badge>
        
        {/* Item Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{item.part_number}</span>
            <span className="text-sm text-slate-600">{item.description}</span>
          </div>
        </div>

        {/* Quantity Display/Edit (only for child items) */}
        {parentBom && (
          <div className="flex items-center gap-2">
            {isEditingQty ? (
              <>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={editedQty}
                  onChange={(e) => setEditedQty(e.target.value)}
                  className="w-20 h-8"
                  autoFocus
                />
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={handleSaveQty}>
                  <Check className="w-4 h-4 text-green-600" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={handleCancelEdit}>
                  <X className="w-4 h-4 text-red-600" />
                </Button>
              </>
            ) : (
              <>
                <span className="font-semibold text-blue-600">Qty: {parentBom.qty_per}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => setIsEditingQty(true)}
                  title="Edit quantity"
                >
                  <Edit2 className="w-4 h-4 text-slate-600" />
                </Button>
              </>
            )}
          </div>
        )}
        
        {/* Component Count Badge */}
        {hasChildren && (
          <Badge variant="outline" className="ml-2">
            {children.length} component{children.length !== 1 ? 's' : ''}
          </Badge>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-1">
          {canAddComponent && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2"
              onClick={() => onAddComponent(item)}
              title="Add component"
            >
              <Plus className="w-4 h-4 text-green-600" />
              <span className="ml-1 text-xs">Add</span>
            </Button>
          )}
          
          {parentBom && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => onDeleteComponent(parentBom.id)}
              title="Delete component"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          )}
        </div>
      </div>

      {/* Render Children */}
      {isExpanded && hasChildren && (
        <div className="border-l-2 border-slate-300 ml-4 pl-2 space-y-2">
          {children.map(bomEntry => {
            const childItem = items.find(i => i.id === bomEntry.child_item_id);
            if (!childItem) return null;
            
            return (
              <BomTreeNode
                key={bomEntry.id}
                item={childItem}
                items={items}
                boms={boms}
                level={level + 1}
                expandedItems={expandedItems}
                toggleExpand={toggleExpand}
                onAddComponent={onAddComponent}
                onDeleteComponent={onDeleteComponent}
                onUpdateQuantity={onUpdateQuantity}
                parentBom={bomEntry}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const BomMasterNew = () => {
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [itemNameFilter, setItemNameFilter] = useState('all');
  const [itemCategoryFilter, setItemCategoryFilter] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});
  const [addDialog, setAddDialog] = useState({ open: false, parentItem: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, bomId: null });
  const [componentCategoryFilter, setComponentCategoryFilter] = useState('all');
  
  const [newComponent, setNewComponent] = useState({
    child_item_id: '',
    qty_per: 1,
    operation_seq: 10
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Reset dependent filters when customer changes
  useEffect(() => {
    setItemNameFilter('all');
    setItemCategoryFilter('all');
  }, [customerFilter]);

  const fetchData = async () => {
    try {
      const [bomRes, itemRes, customerRes] = await Promise.all([
        axios.get(`${API}/boms`),
        axios.get(`${API}/items`),
        axios.get(`${API}/customers`)
      ]);
      setBoms(bomRes.data);
      setItems(itemRes.data);
      setCustomers(customerRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load BOM data');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : '';
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleAddComponent = (parentItem) => {
    setAddDialog({ open: true, parentItem });
    setNewComponent({ child_item_id: '', qty_per: 1, operation_seq: 10 });
    setComponentCategoryFilter('all');
  };

  const handleSaveComponent = async () => {
    if (!newComponent.child_item_id) {
      toast.error('Please select a component');
      return;
    }

    if (addDialog.parentItem.id === newComponent.child_item_id) {
      toast.error('Parent and child cannot be the same item');
      return;
    }

    try {
      await axios.post(`${API}/boms`, {
        parent_item_id: addDialog.parentItem.id,
        child_item_id: newComponent.child_item_id,
        qty_per: parseFloat(newComponent.qty_per),
        operation_seq: parseInt(newComponent.operation_seq)
      });
      
      toast.success('Component added successfully');
      setAddDialog({ open: false, parentItem: null });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add component';
      toast.error(errorMsg);
    }
  };

  const handleUpdateQuantity = async (bomId, newQty) => {
    try {
      await axios.put(`${API}/boms/${bomId}`, { qty_per: newQty });
      toast.success('Quantity updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleDeleteComponent = (bomId) => {
    setDeleteDialog({ open: true, bomId });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/boms/${deleteDialog.bomId}`);
      toast.success('Component deleted successfully');
      setDeleteDialog({ open: false, bomId: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete component');
    }
  };

  // Items belonging to selected customer (for filter derivation)
  const customerScopedItems = customerFilter === 'all'
    ? items
    : items.filter(i => i.customer_id === customerFilter);

  // Unique categories derived from the selected customer's items
  const customerCategories = [...new Set(customerScopedItems.map(i => i.category))].sort();

  // FG items for the item-name searchable dropdown (filtered by customer + category)
  const fgItemsForFilter = customerScopedItems.filter(i =>
    i.category === 'FG' &&
    (itemCategoryFilter === 'all' || i.category === itemCategoryFilter)
  );

  // Only show FG items as root nodes - SFG items appear nested under FG
  const parentItems = items.filter(i => {
    if (i.category !== 'FG') return false;
    if (customerFilter !== 'all' && i.customer_id !== customerFilter) return false;
    if (itemCategoryFilter !== 'all' && i.category !== itemCategoryFilter) return false;
    if (itemNameFilter !== 'all' && i.id !== itemNameFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-medium text-blue-900">Unified BOM Management</p>
          <p className="text-sm text-blue-700">
            Click <strong>"Add"</strong> to add components to any FG/SFG item. 
            Click on quantities to edit inline. 
            Click <strong>"Expand"</strong> to see multi-level hierarchy.
          </p>
        </div>
      </div>

      {/* Single Unified BOM Tree */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-600" />
              <CardTitle>Bill of Materials - Multi-Level View</CardTitle>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Customer Filter */}
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-44" data-testid="bom-customer-filter">
                  <SelectValue placeholder="Filter by Customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Item Category Filter — derived from selected customer's items */}
              <Select
                value={itemCategoryFilter}
                onValueChange={setItemCategoryFilter}
              >
                <SelectTrigger className="w-40" data-testid="bom-item-category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {customerCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Item Name Filter — searchable dropdown derived from customer + category */}
              <SearchableItemSelect
                items={fgItemsForFilter}
                value={itemNameFilter}
                onValueChange={(val) => setItemNameFilter(val || 'all')}
                placeholder="All Items"
                showCategory={false}
                className="w-56"
                dataTestId="bom-item-name-filter"
              />
            </div>
          </div>
          <CardDescription>
            Manage complete BOM hierarchy in one place. Only FG and SFG items can have components.
            {customerFilter !== 'all' && ` Showing BOMs for ${getCustomerName(customerFilter)}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {parentItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>
                {itemNameFilter !== 'all' || itemCategoryFilter !== 'all'
                  ? 'No FG items match the current filters. Try adjusting the item name or category.'
                  : customerFilter !== 'all'
                    ? `No BOMs found for ${getCustomerName(customerFilter)}. Please create FG items for this customer first.`
                    : 'No items with BOMs found. Please create FG or SFG items first in the Item Master.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {parentItems.map(parent => (
                <div key={`tree-${parent.id}`} className="border-l-4 border-purple-500 pl-4">
                  {parent.category === 'FG' && parent.customer_id && (
                    <Badge variant="outline" className="mb-1 bg-blue-50 text-blue-700">
                      {getCustomerName(parent.customer_id)}
                    </Badge>
                  )}
                  <BomTreeNode
                    item={parent}
                    items={items}
                    boms={boms}
                    level={0}
                    expandedItems={expandedItems}
                    toggleExpand={toggleExpand}
                    onAddComponent={handleAddComponent}
                    onDeleteComponent={handleDeleteComponent}
                    onUpdateQuantity={handleUpdateQuantity}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Component Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(open) => setAddDialog({ open, parentItem: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Component</DialogTitle>
            <DialogDescription>
              Add a component to {addDialog.parentItem?.part_number} - {addDialog.parentItem?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Component Item</label>
              <div className="flex gap-2 mb-2">
                <Select value={componentCategoryFilter} onValueChange={setComponentCategoryFilter}>
                  <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="bom-category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="SFG">SFG</SelectItem>
                    <SelectItem value="BOP">BOP</SelectItem>
                    <SelectItem value="RM-Sheet">RM-Sheet</SelectItem>
                    <SelectItem value="RM-Tube">RM-Tube</SelectItem>
                    <SelectItem value="RM-Bar">RM-Bar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <SearchableItemSelect
                items={items.filter(i => i.category !== 'FG' && (componentCategoryFilter === 'all' || i.category === componentCategoryFilter))}
                value={newComponent.child_item_id}
                onValueChange={(value) => setNewComponent({ ...newComponent, child_item_id: value === 'all' ? '' : value })}
                placeholder="Search & select component..."
                showCategory={true}
                dataTestId="bom-component-select"
              />
              <p className="text-xs text-slate-500 mt-1">FG items cannot be added as components</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Quantity Per</label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={newComponent.qty_per}
                  onChange={(e) => setNewComponent({ ...newComponent, qty_per: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Operation Seq</label>
                <Input
                  type="number"
                  min="1"
                  value={newComponent.operation_seq}
                  onChange={(e) => setNewComponent({ ...newComponent, operation_seq: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ open: false, parentItem: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveComponent} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Component
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, bomId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Component?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this component from the BOM. This action cannot be undone and will affect MRP calculations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BomMasterNew;
